/**
 * SamplesPanel.tsx - Universal samples panel for plugins
 * 
 * CHANGE HISTORY:
 * - 251108_1015 Initial creation - Samples UI system
 * 
 * FEATURES:
 * - Display samples from any plugin
 * - Load sample data into tools
 * - Filter and search samples
 * - Sample preview
 */

import React, { useState } from 'react';
import './SamplesPanel.css';

export interface Sample {
  id: string;
  name: string;
  description: string;
  icon?: string;
  data: any;
}

export interface SamplesPanelProps {
  samples: Sample[];
  onLoadSample: (sample: Sample) => void;
  onClose?: () => void;
  title?: string;
}

/**
 * SamplesPanel - Display and load sample data
 */
export function SamplesPanel({
  samples,
  onLoadSample,
  onClose,
  title = 'Sample Data'
}: SamplesPanelProps) {
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSamples = samples.filter(sample =>
    sample.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sample.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLoadSample = (sample: Sample) => {
    onLoadSample(sample);
    setSelectedSample(null);
  };

  return (
    <div className="samples-panel">
      <div className="samples-header">
        <h3>{title}</h3>
        {onClose && (
          <button className="close-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
      </div>

      {samples.length > 3 && (
        <div className="samples-search">
          <input
            type="text"
            placeholder="Search samples..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      )}

      <div className="samples-list">
        {filteredSamples.length === 0 ? (
          <div className="no-samples">
            <p>No samples found</p>
          </div>
        ) : (
          filteredSamples.map((sample) => (
            <div
              key={sample.id}
              className={`sample-item ${selectedSample?.id === sample.id ? 'selected' : ''}`}
              onClick={() => setSelectedSample(sample)}
            >
              <div className="sample-icon">{sample.icon || '📄'}</div>
              <div className="sample-info">
                <div className="sample-name">{sample.name}</div>
                <div className="sample-description">{sample.description}</div>
              </div>
              <button
                className="load-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLoadSample(sample);
                }}
              >
                Load
              </button>
            </div>
          ))
        )}
      </div>

      {selectedSample && (
        <div className="sample-preview">
          <h4>Preview: {selectedSample.name}</h4>
          <pre className="sample-data">
            {JSON.stringify(selectedSample.data, null, 2)}
          </pre>
          <div className="preview-actions">
            <button
              className="load-button-large"
              onClick={() => handleLoadSample(selectedSample)}
            >
              Load This Sample
            </button>
            <button
              className="cancel-button"
              onClick={() => setSelectedSample(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * SamplesButton - Compact button to toggle samples panel
 */
export interface SamplesButtonProps {
  onClick: () => void;
  count?: number;
  disabled?: boolean;
}

export function SamplesButton({ onClick, count, disabled = false }: SamplesButtonProps) {
  return (
    <button
      className="samples-button"
      onClick={onClick}
      disabled={disabled}
      title="Load sample data"
    >
      <span className="button-icon">📦</span>
      <span className="button-text">Samples</span>
      {count !== undefined && count > 0 && (
        <span className="sample-count">{count}</span>
      )}
    </button>
  );
}

export default SamplesPanel;
